// Play analysis helpers — pure functions for determining play outcomes,
// ribbon headlines, and visual parameters. No DOM or canvas dependency.

import { snapToPixel } from '../util/math.js';
import { fieldMetrics, screenXForYard, goalLineXForDirection } from '../field/metrics.js';

export interface RibbonHeadline {
  text: string;
  color: string;
  glow: string;
}

function playResultText(play: any): string {
  return String(play?.result || '').toLowerCase();
}

function replayTurnoverKind(play: any): string {
  return String(play?.arcadeReplay?.metadata?.turnoverKind || '').toLowerCase();
}

function replayCatchResult(play: any): string {
  return String(play?.arcadeReplay?.metadata?.catchResult || '').toLowerCase();
}

export function interceptionHeadline(): RibbonHeadline {
  return { text: 'INTERCEPTION', color: '#ff7aa8', glow: 'rgba(255, 122, 168, 0.5)' };
}

export function fumbleHeadline(): RibbonHeadline {
  return { text: 'FUMBLE', color: '#ff8c69', glow: 'rgba(255, 140, 105, 0.5)' };
}

export function isInterceptionPlay(play: any): boolean {
  if (!play) {
    return false;
  }
  if (String(play.passBranch || '').toLowerCase() === 'interception') {
    return true;
  }
  if (replayTurnoverKind(play) === 'interception' || replayCatchResult(play) === 'interception') {
    return true;
  }
  const resultText = playResultText(play);
  return !!play.turnover
    && (
      !!play.participants?.interceptorId
      || !!play.participants?.interceptorRole
      || /intercept/i.test(resultText)
    );
}

export function isSackPlay(play: any): boolean {
  if (!play) {
    return false;
  }
  if (String(play.passBranch || '').toLowerCase() === 'sack') {
    return true;
  }
  const catchResult = replayCatchResult(play);
  if (catchResult === 'sack' || catchResult === 'strip-sack') {
    return true;
  }
  return /sack/i.test(playResultText(play));
}

export function isCompletedPassPlay(play: any): boolean {
  if (!play || play.family !== 'pass') {
    return false;
  }
  if (play.scramble || isSackPlay(play) || isInterceptionPlay(play)) {
    return false;
  }

  const passBranch = String(play.passBranch || '').toLowerCase();
  if (passBranch === 'completion') {
    return true;
  }
  if (passBranch === 'incomplete' || passBranch === 'sack' || passBranch === 'interception') {
    return false;
  }

  const catchResult = replayCatchResult(play);
  if (catchResult === 'complete') {
    return true;
  }
  if (['drop', 'breakup', 'batted', 'inaccurate', 'interception', 'sack', 'strip-sack'].includes(catchResult)) {
    return false;
  }

  const stopReason = String(play.stopReason || '').toLowerCase();
  if (stopReason === 'incomplete') {
    return false;
  }

  const resultText = playResultText(play);
  if (/ completes |turns .* into a touchdown|hauls in|pulls it in/i.test(` ${resultText} `)) {
    return true;
  }
  if (/ misses |incomplete|throws away|broken up|batted away/i.test(` ${resultText} `)) {
    return false;
  }

  return !!play.participants?.targetId && typeof play.yards === 'number' && play.yards > 0;
}

export function stopReasonForPlay(play: any): string {
  const resultText = playResultText(play);
  if (play.playId === 'pat' && Number(play.patReturnPoints || 0) > 0) {
    return 'pat-return-score';
  }
  if (play.stopReason) {
    if (play.stopReason === 'turnover-on-downs') {
      return resultText.includes('incomplete') ? 'incomplete' : 'tackle';
    }
    return play.stopReason;
  }
  if (play.touchdown || play.defensiveScore) {
    return 'touchdown';
  }
  if (play.safety) {
    return 'safety';
  }
  if (play.playId === 'kneel') {
    return 'kneel';
  }
  if (resultText.includes('incomplete')) {
    return 'incomplete';
  }
  if (resultText.includes('touchback')) {
    return 'touchback';
  }
  if (play.turnover || play.turnoverOnDowns) {
    return 'turnover';
  }
  return 'tackle';
}

export function stopPointForPlay(play: any): number {
  const rawArcadeReplay = play?.arcadeReplay?.source === 'arcade-sim-v1'
    && play?.arcadePlaybackMode === 'raw'
    && Array.isArray(play?.arcadeReplay?.frames)
    && play.arcadeReplay.frames.length > 1
    ? play.arcadeReplay
    : null;
  const stopReason = stopReasonForPlay(play);

  if (rawArcadeReplay) {
    const totalFrames = Math.max(1, rawArcadeReplay.frames.length - 1);
    const touchdownFrame = rawArcadeReplay.events?.find((event: any) => event?.type === 'touchdown')?.simFrame;
    const downFrame = rawArcadeReplay.events?.find((event: any) => event?.type === 'ballcarrier_down')?.simFrame;
    const whistleFrame = rawArcadeReplay.keyEvents?.whistle?.simFrame;
    const replayFrameIndex = stopReason === 'touchdown' || stopReason === 'pat-return-score'
      ? touchdownFrame ?? whistleFrame
      : stopReason === 'tackle' || stopReason === 'turnover' || stopReason === 'turnover-on-downs' || stopReason === 'out-of-bounds' || stopReason === 'safety'
        ? downFrame ?? whistleFrame
        : whistleFrame;

    if (typeof replayFrameIndex === 'number') {
      const stopFrame = Math.max(0, Math.min(totalFrames, replayFrameIndex));
      const minStop = stopReason === 'touchdown' || stopReason === 'pat-return-score'
        ? 0.78
        : stopReason === 'tackle' || isSackPlay(play)
          ? 0.82
          : 0.72;
      return Math.max(minStop, Math.min(0.98, stopFrame / totalFrames));
    }
  }

  switch (stopReason) {
    case 'kneel':
      return 0.68;
    case 'incomplete':
      return 0.74;
    case 'touchback':
      return 0.72;
    case 'dead-short':
      return 0.68;
    case 'out-of-bounds':
      return 0.82;
    case 'safety':
      return 0.82;
    case 'turnover':
    case 'turnover-on-downs':
      return 0.82;
    case 'pat-return-score':
    case 'touchdown':
      return 0.88;
    default:
      return 0.86;
  }
}

export function ribbonHeadlineForPlay(play: any): RibbonHeadline | null {
  if (!play) {
    return null;
  }

  const resultText = playResultText(play);
  const stopReason = stopReasonForPlay(play);
  const kickAttempt = play.playId === 'field-goal' || play.playId === 'pat';
  const kickGood = !!play.fieldGoal?.good || resultText.includes('adds the extra point') || resultText.includes('drills a');

  if (play.twoPointAttempt) {
    if (play.twoPointDefensiveReturn) {
      return { text: 'DEFENSE SCORES 2', color: '#ffe066', glow: 'rgba(255, 224, 102, 0.55)' };
    }
    return play.twoPointSuccess
      ? { text: 'TWO-POINT TRY GOOD', color: '#9cff8a', glow: 'rgba(156, 255, 138, 0.48)' }
      : { text: 'TWO-POINT TRY NO GOOD', color: '#ff8f6b', glow: 'rgba(255, 143, 107, 0.48)' };
  }
  if (play.playId === 'pat' && Number(play.patReturnPoints || 0) > 0) {
    return { text: 'DEFENSE SCORES 2', color: '#ffe066', glow: 'rgba(255, 224, 102, 0.55)' };
  }
  if (stopReason === 'touchdown' || play.touchdown || play.defensiveScore || play.returnTouchdown) {
    return { text: 'TOUCHDOWN', color: '#ffe066', glow: 'rgba(255, 224, 102, 0.55)' };
  }
  if (kickAttempt) {
    return kickGood
      ? { text: 'KICK IS GOOD', color: '#9cff8a', glow: 'rgba(156, 255, 138, 0.48)' }
      : { text: 'KICK IS NO GOOD', color: '#ff8f6b', glow: 'rgba(255, 143, 107, 0.48)' };
  }
  if (stopReason === 'safety' || play.safety) {
    return { text: 'SAFETY', color: '#ffb347', glow: 'rgba(255, 179, 71, 0.52)' };
  }
  if (isInterceptionPlay(play)) {
    return interceptionHeadline();
  }
  if (isFumbleTurnover(play)) {
    return fumbleHeadline();
  }
  if (isSackPlay(play)) {
    return { text: 'SACK', color: '#ffd166', glow: 'rgba(255, 209, 102, 0.48)' };
  }
  if (play.scramble) {
    return null;
  }
  if (play.family === 'pass' && stopReason === 'incomplete') {
    return { text: 'INCOMPLETE', color: '#dbe8ff', glow: 'rgba(219, 232, 255, 0.44)' };
  }
  if (play.family === 'pass') {
    return { text: 'COMPLETE', color: '#8ef6ff', glow: 'rgba(142, 246, 255, 0.46)' };
  }
  return null;
}

export function visualYardsForPlay(play: any): number {
  if (typeof play.visualYards === 'number') {
    return play.visualYards;
  }
  return typeof play.yards === 'number' ? play.yards : 0;
}

export function isInterceptionReturn(play: any): boolean {
  return !!play?.turnover && isInterceptionPlay(play);
}

export function fumbleRecoverySide(play: any): 'offense' | 'defense' | null {
  const replayRecoveredBySide = String(play?.arcadeReplay?.metadata?.recoveredBySide || '').toLowerCase();
  if (replayRecoveredBySide === 'offense' || replayRecoveredBySide === 'defense') {
    return replayRecoveredBySide;
  }
  const resultText = playResultText(play);
  if (/offense falls on it|offense recovers|retains possession|falls on it/i.test(resultText)) {
    return 'offense';
  }
  if (isFumbleTurnover(play)) {
    return 'defense';
  }
  return null;
}

export function isDefensiveFumbleRecovery(play: any): boolean {
  return fumbleRecoverySide(play) === 'defense';
}

export function fumbleRecoveryHeadline(play: any): RibbonHeadline | null {
  const side = fumbleRecoverySide(play);
  if (side === 'defense') {
    return fumbleHeadline();
  }
  if (side === 'offense') {
    return { text: 'OFFENSE RECOVERS', color: '#ffd166', glow: 'rgba(255, 209, 102, 0.48)' };
  }
  return null;
}

export function isFumbleTurnover(play: any): boolean {
  if (!play?.turnover) {
    return false;
  }
  if (replayTurnoverKind(play) === 'fumble') {
    return true;
  }
  return /fumble|strip-sack|loses the ball/i.test(playResultText(play));
}

export function isReturnTouchdown(play: any): boolean {
  if (!play) {
    return false;
  }
  if (play.returnTouchdown) {
    return true;
  }
  return (play.playId === 'kickoff' || play.playId === 'punt')
    && (play.touchdown || stopReasonForPlay(play) === 'touchdown');
}

export function turnoverReturnYards(play: any): number {
  if (!play) {
    return 0;
  }
  if (typeof play.returnYards === 'number') {
    return Math.max(0, play.returnYards);
  }
  const resultText = String(play.result || '');
  const returnMatch = resultText.match(/returns it (\d+) yards/i);
  if (returnMatch) {
    return Math.max(0, Number(returnMatch[1]) || 0);
  }
  if (isInterceptionReturn(play) && stopReasonForPlay(play) === 'touchdown') {
    return Math.max(0, play.catchYardLine ?? play.startYardLine ?? 0);
  }
  return 0;
}

export function turnoverReturnEndYardLine(play: any): number {
  if (!play) {
    return 0;
  }
  if (typeof play.returnEndYardLine === 'number') {
    return play.returnEndYardLine;
  }
  const turnoverSpot = turnoverSpotYardLine(play);
  return Math.max(0, turnoverSpot - turnoverReturnYards(play));
}

export function turnoverSpotYardLine(play: any): number {
  if (!play) {
    return 0;
  }
  if (typeof play.turnoverSpotYardLine === 'number') {
    return play.turnoverSpotYardLine;
  }
  if (typeof play.catchYardLine === 'number') {
    return typeof play.catchYardLine === 'number'
      ? play.catchYardLine
      : (typeof play.endYardLine === 'number' ? play.endYardLine : (play.startYardLine ?? 0));
  }
  if (typeof play.returnEndYardLine === 'number') {
    return Math.max(0, Math.min(100, play.returnEndYardLine + turnoverReturnYards(play)));
  }
  return typeof play.endYardLine === 'number' ? play.endYardLine : (play.startYardLine ?? 0);
}

export function interceptionReturnEndYardLine(play: any): number {
  return turnoverReturnEndYardLine(play);
}

export function touchdownRunoutDepth(play: any): number {
  const token = String(play.sequence || 0) + ':' + String(play.playId || 'score') + ':' + String(play.offenseSimulationId || play.offense || '') + ':' + String(play.result || '');
  let hash = 0;
  for (let index = 0; index < token.length; index += 1) {
    hash = (hash * 33 + token.charCodeAt(index)) % 9973;
  }
  const baseDepth = 1.1 + (hash % 6) * 0.7;
  const momentumDepth = Math.min(2.6, Math.max(0, (visualYardsForPlay(play) - 6) * 0.12));
  return Math.max(1.1, Math.min(7.2, baseDepth + momentumDepth));
}

export function visualEndXForPlay(play: any, direction: number, canvasHeight?: number): number {
  const resolvedEndYardLine = (isInterceptionReturn(play) || isFumbleTurnover(play))
    ? turnoverReturnEndYardLine(play)
    : (play.endYardLine ?? play.startYardLine ?? 0);
  const actualEndX = screenXForYard(resolvedEndYardLine, direction, canvasHeight);
  const stopReason = stopReasonForPlay(play);
  if (stopReason !== 'touchdown' && stopReason !== 'pat-return-score') {
    return actualEndX;
  }
  const m = fieldMetrics(canvasHeight);
  const scoringDirection = (stopReason === 'pat-return-score' || !!play.returnTouchdown || !!play.defensiveScore) ? -direction : direction;
  return snapToPixel(goalLineXForDirection(scoringDirection, canvasHeight) + scoringDirection * m.yardWidth * touchdownRunoutDepth(play));
}

export function isScrimmageSnap(play: any): boolean {
  return play.family === 'run' || play.family === 'pass';
}

export function isSpecialTeamsPlay(play: any): boolean {
  return play.playId === 'kickoff' || play.playId === 'punt' || play.playId === 'field-goal' || play.playId === 'pat';
}

// Deterministic hash for play-specific visual decisions
export function playHash(play: any, salt: string = ''): number {
  const token = salt + ':' + String(play.sequence || 0) + ':' + String(play.playId || '') + ':' + String(play.offenseSimulationId || play.offense || '') + ':' + String(play.result || '');
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) {
    hash = (hash * 33 + token.charCodeAt(i)) % 9973;
  }
  return hash;
}

export function playChance(play: any, salt: string, threshold: number): boolean {
  return (playHash(play, salt) % 100) / 100 < threshold;
}

export function pickDeterministic<T>(items: T[], play: any, salt: string, fallback: T | null = null): T | null {
  if (!items.length) return fallback;
  return items[playHash(play, salt) % items.length] ?? fallback;
}

export function pickDeterministicGroup<T>(items: T[], play: any, salt: string, count: number = 1): T[] {
  if (!items.length || count <= 0) return [];
  const start = playHash(play, salt) % items.length;
  const result: T[] = [];
  for (let i = 0; i < Math.min(count, items.length); i++) {
    result.push(items[(start + i) % items.length]);
  }
  return result;
}
